import React, { useMemo } from 'react';
import { ThirtyDayCalendar } from './ThirtyDayCalendar';
import { type AsOfDateSetting, assertNotNil, Dates } from 'venn-utils';
import moment from 'moment';
import { useThirtyDayCalendar } from './useThirtyDayCalendar';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Match } from 'effect';
import { ThirtyDayCalendarActions } from './useThirtyDayCalendarActions';
import { Separator } from '@/components/ui/separator';
import { max, min, some } from 'lodash';
import { Tooltip, TooltipContentWithArrow, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';

type ThirtyDayCalendarPickerProps = {
  allocationsAsOfDate: AsOfDateSetting;
  asOfDateTimestamp: number;
  lastUploadedDate: number;
  currentDate: number;
  allocationDates: number[];
};

const buttonVariants = cva(cn('flex', 'items-center', 'justify-center', 'text-black', 'p-[0.5rem]', 'rounded-[4px]'), {
  variants: {
    state: {
      selected: '[&]:text-white font-bold bg-venn-deep-dark-blue',
      disabled: 'text-venn-grey-30 hover:text-venn-grey-30 hover:cursor-not-allowed',
      default: 'hover:bg-venn-grey-10',
    },
    variant: {
      cell: '',
      pill: '[&]:border [&]:border-solid [&]:border-venn-grey-30',
    },
  },
});

const toMonthName = (monthValue: number) => {
  const ALL_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return assertNotNil(ALL_MONTHS[monthValue]);
};

export const ThirtyDayCalendarPicker = ({
  allocationsAsOfDate,
  asOfDateTimestamp,
  lastUploadedDate,
  currentDate,
  allocationDates,
}: ThirtyDayCalendarPickerProps) => {
  const {
    performAction,
    state: { selectedYear, selectedMonth, monthsToDisplay, monthsWithAllocation, allYears, cells },
  } = useThirtyDayCalendar({
    asOfDateTimestamp,
    allocationDates,
  });
  const visibleMonth = moment.utc().year(selectedYear).month(selectedMonth);
  const allocationDatesMoment = allocationDates.map((date) => moment.utc(date));
  const isCellDisabled = (date: moment.Moment) =>
    !some(allocationDatesMoment, (allocationDate) => moment.utc(allocationDate).isSame(date, 'day'));
  const currentMonthIsFinalMonth = useMemo(() => {
    const currentMonth = moment.utc().year(selectedYear).month(selectedMonth);
    const finalMonth = assertNotNil(max(allocationDates));
    return currentMonth.isSame(finalMonth, 'month') && currentMonth.isSame(finalMonth, 'year');
  }, [selectedMonth, selectedYear, allocationDates]);
  const currentMonthIsFirstMonth = useMemo(() => {
    const currentMonth = moment.utc().year(selectedYear).month(selectedMonth);
    const firstMonth = assertNotNil(min(allocationDates));
    return currentMonth.isSame(firstMonth, 'month') && currentMonth.isSame(firstMonth, 'year');
  }, [selectedMonth, selectedYear, allocationDates]);
  const columnClass = cn(
    'flex',
    'flex-col',
    'gap-[0.5rem]',
    'p-[8px]',
    'items-start',
    'text-black',
    'font-roboto',
    'text-[14px]',
  );
  const getMonthVariant = (value: number) =>
    Match.value(value).pipe(
      Match.when(
        (month) => month === selectedMonth,
        () => 'selected' as const,
      ),
      Match.when(
        (month) => !monthsWithAllocation.includes(month),
        () => 'disabled' as const,
      ),
      Match.orElse(() => 'default' as const),
    );
  const getYearVariant = (value: number) =>
    Match.value(value).pipe(
      Match.when(
        (yearValue) => yearValue === selectedYear,
        () => 'selected' as const,
      ),
      Match.orElse(() => 'default' as const),
    );
  return (
    <div className="flex items-stretch h-[416px]">
      <ScrollArea>
        <div className={cn(columnClass, allYears.length > 12 ? 'mr-[6px]' : '')}>
          {allYears.map((year) => (
            <button
              type="button"
              onClick={() => performAction(ThirtyDayCalendarActions.ChooseYear({ year }))}
              key={year}
              className={buttonVariants({
                state: getYearVariant(year),
                variant: 'cell',
              })}
            >
              {year}
            </button>
          ))}
        </div>
        <ScrollBar forceMount />
      </ScrollArea>
      <Separator orientation="vertical" className="bg-venn-grey-30" />
      <div className={columnClass}>
        {monthsToDisplay.map((monthValue) => {
          const state = getMonthVariant(monthValue);
          return (
            <button
              type="button"
              disabled={state === 'disabled'}
              onClick={() => performAction(ThirtyDayCalendarActions.ChooseMonth({ month: monthValue }))}
              className={buttonVariants({
                state,
                variant: 'cell',
              })}
              key={monthValue}
            >
              {toMonthName(monthValue)}
            </button>
          );
        })}
      </div>
      <Separator orientation="vertical" className="bg-venn-grey-30" />
      <div className="flex flex-col p-[16px] gap-[8px]">
        <div className="flex flex-row justify-end gap-[8px]">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={buttonVariants({
                  state: Match.value(allocationsAsOfDate).pipe(
                    Match.tag('Current', () => 'selected' as const),
                    Match.orElse(() => 'default' as const),
                  ),
                  variant: 'pill',
                })}
                onClick={() => performAction(ThirtyDayCalendarActions.ChooseCurrentAllocationAsOfDate())}
              >
                Current
              </button>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContentWithArrow>{Dates.toDDMMMYYYY(currentDate)}</TooltipContentWithArrow>
            </TooltipPortal>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={buttonVariants({
                  state: Match.value(allocationsAsOfDate).pipe(
                    Match.tag('LastUploaded', () => 'selected' as const),
                    Match.orElse(() => 'default' as const),
                  ),
                  variant: 'pill',
                })}
                onClick={() => performAction(ThirtyDayCalendarActions.ChooseLastUploadedAllocationAsOfDate())}
              >
                Last Uploaded
              </button>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContentWithArrow>{Dates.toDDMMMYYYY(lastUploadedDate)}</TooltipContentWithArrow>
            </TooltipPortal>
          </Tooltip>
        </div>
        <ThirtyDayCalendar
          cells={cells}
          visibleMonth={visibleMonth}
          handlePreviousMonth={() => performAction(ThirtyDayCalendarActions.ChoosePreviousMonth())}
          handleNextMonth={() => performAction(ThirtyDayCalendarActions.ChooseNextMonth())}
          handleSelect={(date) => performAction(ThirtyDayCalendarActions.ChooseDay({ dateTimestamp: date.valueOf() }))}
          isCellDisabled={isCellDisabled}
          valueDate={moment.utc(asOfDateTimestamp)}
          isNextMonthDisabled={currentMonthIsFinalMonth}
          isPreviousMonthDisabled={currentMonthIsFirstMonth}
        />
      </div>
    </div>
  );
};
